import React, { useContext, useEffect, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import Header, { HeaderVariant } from '../Elements/Header'
import { Registrant } from '../../swagger/models/Registrant'
import TableHeaders from '../Interfaces/TableHeaders'
import TableHeader from '../Table/TableHeader'
import { getCommaSeparatedList } from '../../utils/getCommaSeparatedList'
import NoResultsFound from '../Table/NoResultsFound'
import PrintIcon from '@mui/icons-material/Print'
import SpaceBetweenSection from '../Elements/SpaceBetweenSection'
import IconTextButton, {
  IconTextButtonVariant,
} from '../Buttons/IconTextButton'
import { useNavigate, useParams } from 'react-router'
import { Box, styled } from '@mui/system'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../Table/ActionableTable'
import { useAuth } from '../Routes/AuthProvider'
import RegistrantModal from '../Modals/RegistrantModal'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { Action } from '../Table/RowActions'
import {
  GridInputRowSelectionModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid'
import {
  extractedErrorObject,
  PaginationResponse,
  registrantsApi,
  RegistrantsToUpdate,
} from '../../api/swagger'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import { useSnackbarContext } from '../Context/SnackbarContext'
import { LoadingContext } from '../Context/LoadingContext'
import useLoadingContext from '../../hooks/useLoadingContext'
import LoadingProgress from '../Elements/LoadingProgress'
import { dateToDashString } from '../../utils/dateUtility'
import { Card, IconButton, Tooltip } from '@mui/material'
import CardFormHeader from '../Card/CardFormHeader'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import { useMountEffect } from '../../hooks/useMountEffect'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import ConfirmationModal from '../Modals/ConfirmationModal'
import ActionButtons from '../Buttons/ActionButtons'
import { TextButtonVariant } from '../Buttons/TextButton'
import { openMailClient } from '@utils/openMailClient'

const RegistrantsTableBody = styled(TableCell)(({ theme }) => ({
  color: theme.palette.primary.main,
}))

interface RegistrantsTableProps {
  registrants: Registrant[]
  eventRegistrants: number
  eventCapacity?: number
  refetch?: () => void
  pagination?: PaginationResponse
  handleRegistrantPagination?: (pagination: PaginationResponse) => void
}

const RegistrantsTable: React.FC<RegistrantsTableProps> = ({
  registrants,
  eventRegistrants,
  eventCapacity,
  refetch,
  pagination,
  handleRegistrantPagination,
}) => {
  const { handleError } = useAppSnackbar()
  const { addLoadingIds, loadingIds } = useContext(LoadingContext)
  const { eventKey } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { featureAbility, permissionAbility } = useAuth()
  const denominator = `/${eventCapacity}`
  const title = `Registrants (${eventRegistrants}${
    !!eventCapacity ? denominator : ''
  })`

  const { setSnackbarState, setSnackbarMessage, setSnackbarSeverity } =
    useSnackbarContext()
  const theme = useTheme()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedRegistrant, setSelectedRegistrant] = useState<Registrant>()
  const [selectedRegistrants, setSelectedRegistrants] =
    useState<Array<RegistrantsToUpdate>>()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const { RegistrantsTable } = useLoadingIds()

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridInputRowSelectionModel>([])

  const onRowSelectionModelChange = (
    newRowSelectionModel: GridInputRowSelectionModel
  ) => {
    setRowSelectionModel(newRowSelectionModel)
  }

  const updateRegistrantsLoadingId = RegistrantsTable.updateRegistrants
  const updateRegistrantLoadingId = RegistrantsTable.updateRegistrant
  /**
   * This concatenates an extra information since the same operationId
   *  cannot be used in the same component scope
   * */
  const updateRegistrantAttendingLoadingId = `${RegistrantsTable.updateRegistrant}-attending`

  const ActionableTableColumnsHeader: ActionableTableColumn[] = [
    {
      fieldName: 'firstName',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'lastName',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'roles',
      columnHeaderName: t('Events.Registrants.Table.Header.Role', 'Role'),
      valueFormatter: (
        params: GridValueFormatterParams<string[] | undefined>
      ) => {
        const roles = params?.value
        return roles && roles.length > 0
          ? roles && roles.length > 1
            ? getCommaSeparatedList(roles)
            : roles
          : t('Events.Registrants.Table.NoRole', 'None')
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'email',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'phone',
      columnHeaderName: t(
        'Events.Registrants.Table.Header.PhoneNumber',
        'Phone Number'
      ),
      sortable: false,
    },
    {
      fieldName: 'createdDate',
      columnHeaderName: t(
        'Events.Registrants.Table.Header.DateRegistered',
        'Date Registered'
      ),
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return dateToDashString(params.value)
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'attending',
      columnHeaderName: t(
        'Events.Registrants.Table.Header.Attending',
        'Attending'
      ),
      valueFormatter: (params: GridValueFormatterParams<boolean>) => {
        return params.value ? 'Yes' : 'No'
      },
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'isMember',
      columnHeaderName: t('Events.Registrants.Table.Header.Status', 'Status'),
      valueFormatter: (params: GridValueFormatterParams<boolean>) => {
        return params.value ? 'Member' : 'None'
      },
      align: 'center',
      headerAlign: 'center',
      sortable: false,
    },
  ]
  const tableHeaders: TableHeaders[] = [
    {
      label: t('Events.Registrants.Table.Header.FirstName', 'First Name'),
      align: 'left',
    },
    {
      label: t('Events.Registrants.Table.Header.LastName', 'Last Name'),
      align: 'left',
    },
    {
      label: t('Events.Registrants.Table.Header.Role', 'Role'),
      align: 'left',
    },
    {
      label: t('Events.Registrants.Table.Header.Email', 'Email'),
      align: 'left',
    },
    {
      label: t('Events.Registrants.Table.Header.PhoneNumber', 'Phone Number'),
      align: 'left',
    },
  ]

  const handleEditRegistrant = (registrant?: Registrant) => {
    setSelectedRegistrant(registrant)
    setIsModalOpen(true)
  }

  const closeModal = (options?: { refetch?: boolean }) => {
    setIsModalOpen(false)
    setSelectedRegistrant(undefined)
    if (options?.refetch) {
      refetch?.()
    }
  }

  const username = localStorage.getItem('username') || ''

  const rowActions: Action<Registrant>[] = []
  const updateSelectedRegistrant = (row: Registrant | undefined) => {
    setSelectedRegistrant(row)
    addLoadingIds([updateRegistrantAttendingLoadingId])
  }

  const navigateToFamilyProfilePage = (registrant?: Registrant) => {
    navigate({
      pathname: `/family-profile/${registrant?.userKey}`,
    })
  }

  if (permissionAbility.can('edit', 'Registrant')) {
    rowActions.push({
      actionName: t('Events.Registrants.Table.RowAction.Edit', 'Edit'),
      actionFunction: handleEditRegistrant,
      actionKey: `edit`,
    })
    rowActions.push({
      actionName: 'attending',
      actionFunction: updateSelectedRegistrant,
      actionKey: 'attending',
      dynamicActionName: {
        fieldNameUseForFlip: 'attending',
        switchableActionsNames: [
          { flipValue: true, actionName: 'Not attending' },
          { flipValue: false, actionName: 'Attending' },
          // In the case the attending value is null menuItem name should be attending
          { flipValue: undefined, actionName: 'Attending' },
        ],
      },
    })
  }
  if (permissionAbility.can('accessUsersTab', 'Admin')) {
    rowActions.push({
      actionName: 'Navigate to family profile',
      actionFunction: navigateToFamilyProfilePage,
      actionKey: 'Navigate to family profile',
      hide: (row) => !row?.isMember,
    })
  }

  const defaultErrorMessage = t(
    'Registrant.RegistrantUpdate.ErrorMessage',
    'Something went wrong while updating the registrant attendance.'
  )

  const updateRegistrantAttending = async (): Promise<void> => {
    try {
      if (selectedRegistrant) {
        await registrantsApi.updateRegistrant({
          body: {
            ...selectedRegistrant,
            registrantKey: Number(selectedRegistrant.id),
            attending: !selectedRegistrant.attending,
          },
        })
        refetch?.()
        setSnackbarState?.(true)
        setSnackbarMessage?.(
          t(
            'RegistrantModal.UpdateRegistrant.SuccessMessage',
            'Registrant attendance was successfully updated.'
          )
        )
        setSnackbarSeverity?.(SnackbarSeverity.Success)
      }
    } catch (error) {
      const errorObject = (await extractedErrorObject(error)) ?? {
        code: 'Unknown Code',
        message: (error as unknown as Error).message ?? defaultErrorMessage,
      }
      setSnackbarSeverity(SnackbarSeverity.Error)
      setSnackbarMessage(errorObject.message)
      setSnackbarState(true)
    }
  }

  useLoadingContext({
    asyncFunction: updateRegistrantAttending,
    loadingId: updateRegistrantAttendingLoadingId,
  })

  const updateRegistrantsAttending = async (): Promise<void> => {
    try {
      if (selectedRegistrants) {
        await registrantsApi.updateRegistrants({
          body: {
            registrants: selectedRegistrants,
          },
        })
        refetch?.()
        setSnackbarState?.(true)
        setSnackbarMessage?.(
          t(
            'RegistrantModal.UpdateRegistrants.SuccessMessage',
            'Registrant attendance was successfully updated.'
          )
        )
        setSnackbarSeverity?.(SnackbarSeverity.Success)
      }
    } catch (error) {
      await handleError(error, defaultErrorMessage)
    }
  }

  const { triggerFetch } = useLoadingContext({
    loadingId: updateRegistrantsLoadingId,
    asyncFunction: updateRegistrantsAttending,
  })

  useMountEffect(() => {
    triggerFetch()
  })

  useEffect(() => {
    triggerFetch()
  }, [selectedRegistrants, triggerFetch])

  if (loadingIds.has(updateRegistrantAttendingLoadingId)) {
    return <LoadingProgress />
  }

  const printReports = async () => {
    navigate({
      pathname: `/events/event-details/${eventKey}/registrants-report`,
    })
  }

  const handleConfirmationClose = () => {
    setShowConfirmationModal(false)
  }

  /**
   * Handles the user's confirmation to email all visible registrants
   * whose status is "Attending".
   */
  const handleAcceptConfirmation = (event: React.FormEvent<HTMLDivElement>) => {
    /** Prevent the default form submission behavior (e.g. page reload) */
    event.preventDefault()

    /** Filter the currently visible registrants to only those marked as attending */
    const registrantsAttending = registrants?.filter(
      ({ attending }) => !!attending
    )

    // Close the confirmation modal or reset its state
    handleConfirmationClose()
    /**
     * Opens the user's default email client using a `mailto:` link.
     *
     * - The first available email is set as the primary recipient (`to`).
     * - All attending registrants are included as BCC recipients to prevent
     *   exposing the full recipient list.
     */
    return openMailClient({
      to: [username],
      bcc: registrantsAttending.map(({ email }) => email) ?? [],
    })
  }

  const headerMessage = `${t(
    'RegistrantTable.ConfirmationModal.Header',
    'Are you sure you want to email all the registrants visible on your screen?'
  )}`

  const confirmationBodyMessage = (
    <Typography variant="body1" component="p" align="center">
      {t(
        'RegistrantTable.ConfirmationModal.Body',
        `To email all the registrants visible on your screen, click "Yes, I'm Sure".`
      )}
    </Typography>
  )

  const confirmationButtons = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.YesImSure}
      secondaryButtonLabel={TextButtonVariant.NoCancel}
      secondaryClick={handleConfirmationClose}
    />
  )

  const SentEmailToRegistrantsConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: headerMessage,
    dialogContent: confirmationBodyMessage,
    dialogActions: confirmationButtons,
    handleFormSubmit: handleAcceptConfirmation,
  }

  return (
    <section aria-labelledby="registrants">
      <ConfirmationModal {...SentEmailToRegistrantsConfirmationProps} />

      <RegistrantModal
        isOpen={isModalOpen}
        onClose={closeModal}
        initialRegistrant={selectedRegistrant as Registrant}
        primaryButtonLoadingId={updateRegistrantLoadingId}
      />
      <SpaceBetweenSection
        isCentered
        left={<Header id="registrants" headerName={title} component="h3" />}
        right={
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <IconTextButton
              id="reports"
              variant={IconTextButtonVariant.Report}
              startIcon={
                <PrintIcon
                  sx={{
                    color: theme.palette.primary.dark,
                  }}
                />
              }
              onClick={printReports}
            />
            <ContainedButton
              fullWidth
              id="emailRegistrantsButton"
              variant={ContainedButtonVariant.EmailRegistrants}
              onClick={() => setShowConfirmationModal(true)}
              disabled={!registrants.some(({ attending }) => !!attending)}
              css={{
                width: 'auto',
                [theme.breakpoints.down('sm')]: {
                  margin: theme.spacing(1, 0, 2.5, 0),
                },
              }}
            />
          </Box>
        }
      ></SpaceBetweenSection>
      {featureAbility.can('registrantActionableTable', 'Feature') ? (
        <Card>
          <Box>
            <CardFormHeader
              header={
                <Header
                  id="registrants-header"
                  headerName={t(
                    'EventRegistrantCard.RegistrantsTable.Title',
                    'Registrants'
                  )}
                  component="h2"
                  variant={HeaderVariant.Card}
                />
              }
              buttons={
                <Box mr={2} sx={{ display: 'flex', gap: theme.spacing(0.4) }}>
                  {permissionAbility.can('edit', 'Registrant') && (
                    <>
                      <Tooltip
                        title={t(
                          'EventRegistrantCard.Tooltip.AddAttending',
                          'Add attending'
                        )}
                      >
                        <IconButton
                          aria-label="Add attending"
                          onClick={() => {
                            setSelectedRegistrants(
                              (rowSelectionModel as number[]).map(
                                (registrantKey) => {
                                  return { registrantKey, attending: true }
                                }
                              ) as unknown as RegistrantsToUpdate[]
                            )
                          }}
                        >
                          <EventAvailableIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          'EventRegistrantCard.Tooltip.NotAttending',
                          'Not attending'
                        )}
                      >
                        <IconButton
                          aria-label="Not attending"
                          onClick={() => {
                            setSelectedRegistrants(
                              (rowSelectionModel as number[]).map(
                                (registrantKey) => {
                                  return { registrantKey, attending: false }
                                }
                              ) as unknown as RegistrantsToUpdate[]
                            )
                          }}
                        >
                          <EventBusyIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              }
            />
          </Box>
          <Box>
            <ActionableTable
              checkboxSelection={registrants.length > 0}
              columns={ActionableTableColumnsHeader}
              rows={registrants}
              rowActions={rowActions.length ? rowActions : undefined}
              noResultsMessage={t(
                'Events.Registrants.Table.NoRegistrants',
                'No registrants for this event'
              )}
              pagination={pagination}
              handlePaginationChange={handleRegistrantPagination}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={onRowSelectionModelChange}
            />
          </Box>
        </Card>
      ) : (
        <TableContainer
          sx={{
            padding: theme.spacing(3, 4),
          }}
          component={Paper}
        >
          <Table aria-labelledby={title}>
            <TableHead>
              <TableHeader tableHeaders={tableHeaders} />
            </TableHead>
            <TableBody>
              {registrants.length > 0 ? (
                registrants.map((row) => (
                  <TableRow key={row.id}>
                    <RegistrantsTableBody>
                      <Typography variant="body1" component="p">
                        {row.firstName}
                      </Typography>
                    </RegistrantsTableBody>
                    <RegistrantsTableBody>
                      <Typography variant="body1" component="p">
                        {row.lastName}
                      </Typography>
                    </RegistrantsTableBody>
                    <RegistrantsTableBody>
                      <Typography variant="body1" component="p">
                        {row.roles && row.roles.length > 0
                          ? row.roles && row.roles.length > 1
                            ? getCommaSeparatedList(row.roles)
                            : row.roles
                          : t('Events.Registrants.Table.NoRole', 'None')}
                      </Typography>
                    </RegistrantsTableBody>
                    <RegistrantsTableBody>
                      <Typography variant="body1" component="p">
                        {row.email}
                      </Typography>
                    </RegistrantsTableBody>
                    <RegistrantsTableBody>
                      <Typography variant="body1" component="p">
                        {row.phone}
                      </Typography>
                    </RegistrantsTableBody>
                  </TableRow>
                ))
              ) : (
                <NoResultsFound
                  message={t(
                    'Events.Registrants.Table.NoRegistrants',
                    'No Registrants For This Event'
                  )}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </section>
  )
}

export default RegistrantsTable
